import React, { useContext } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import LinkTranslated from '@components/shared/LinkTranslated';
import { NavPreviewItem, NavPreviewItemsWrapper } from '@components/shared/SharedStyles';
import SocialIcons from '@components/shared/SocialIcons';
import { SFMBodyMD } from '@components/shared/Typography';
import { HELP_URL, VITALS_URL } from '@constants';
import { PageOptions, NavBarCategory, NavStatus, NavigationItem, NavigationPreview, NavigationSection } from '@contracts';
import { NavContext } from './Context';
import NavLink from './NavLink';
import NavSearch from './NavSearch';

interface Props {
  periodCare: NavigationSection;
  vaginalHealth: NavigationSection;
  accessories: NavigationSection;
  aboutUs: NavigationSection;
  discover: NavigationSection;
  productCategories: NavigationPreview[];
  policiesMenuItems: NavigationItem[];
}

const NavMainMenuMobile: React.FC<Props> = ({ periodCare, vaginalHealth, accessories, aboutUs, discover, productCategories, policiesMenuItems }) => {
  const { t } = useTranslation();
  const { navState, navDispatch } = useContext(NavContext);
  const { navbarSelectedCategories } = navState;
  const isSearch = navbarSelectedCategories?.length ? navbarSelectedCategories.includes(NavBarCategory.Search) : false;
  const shopAllLink = '/products/';

  return (
    <MobileMenuWrapper>
      {isSearch ? (
        <NavSearch />
      ) : (
        <>
          <TopLinks>
            <NavLink
              text={t('components.period-care')}
              category={NavBarCategory.Shop}
              internaLinkElements={[
                <NavLink key={`nav-link-shop-all`} text={t('components.shop-all')} navLink={shopAllLink} isInternal />,
                ...periodCare.items.map((item) => (
                  <NavLink
                    key={`nav-link-${item.title}`}
                    text={t(item.title)}
                    navLink={item.link}
                    isNew={item.is_new}
                    color={item.text_color}
                    isInternal
                  />
                )),
                <NavLink key={`nav-link-${accessories.title}`} text={t(accessories.title || '')} navLink={accessories.shopAllLink} isInternal />
              ]}
            />
            <NavLink
              text={t(vaginalHealth.title || '')}
              category={NavBarCategory.VaginalHealth}
              internaLinkElements={[
                <NavLink key={`nav-link-shop-all`} text={t('components.shop-all')} navLink={vaginalHealth.shopAllLink} isInternal />,
                ...vaginalHealth.items.map((item) => (
                  <NavLink
                    key={`nav-link-${item.title}`}
                    text={t(item.title)}
                    navLink={item.link}
                    isNew={item.is_new}
                    color={item.text_color}
                    isInternal
                  />
                ))
              ]}
            />
            <NavLink text={t('components.period-pain-clinic-old')} navLink={`/${PageOptions.PeriodPainClinic}/`} />
            <NavLink
              text={t(aboutUs.title || '')}
              category={NavBarCategory.AboutUs}
              internaLinkElements={[
                ...aboutUs.items.map((item) => (
                  <NavLink
                    key={`nav-link-${item.title}`}
                    text={t(item.title)}
                    navLink={item.link}
                    isNew={item.is_new}
                    color={item.text_color}
                    isInternal
                  />
                )),
                ...discover.items.map((item) => (
                  <NavLink
                    key={`nav-link-${item.title}`}
                    text={t(item.title)}
                    navLink={item.link}
                    isNew={item.is_new}
                    color={item.text_color}
                    isInternal
                  />
                ))
              ]}
            />
            <NavLink text={t('components.help-center-link')} navLink={HELP_URL} />
            <NavLink text={t('components.vitals-blog')} navLink={VITALS_URL} />
          </TopLinks>
          <BottomLinks>
            <NavPreviewItemsWrapper>
              {productCategories.map((preview) => (
                <NavPreviewItem
                  key={`nav-preview-link-${preview.link}`}
                  to={preview.link}
                  onClick={() => {
                    navDispatch({ type: 'setStatus', data: NavStatus.Default });
                    navDispatch({ type: 'removeNavbarSelectedCategories' });
                  }}
                >
                  <img src={preview.image} alt={preview.imageAlt} />
                  <SFMBodyMD $bold>{t(preview.title)}</SFMBodyMD>
                </NavPreviewItem>
              ))}
            </NavPreviewItemsWrapper>
            <SocialIconsWrapper>
              <SocialIcons size={26} color={Colors.lilac} />
            </SocialIconsWrapper>
            <PolicyLinks>
              {policiesMenuItems.map((item, i) => (
                <LinkTranslated to={item.link ?? ''} targetBlank key={i}>
                  {t(item.title)}
                </LinkTranslated>
              ))}
            </PolicyLinks>
          </BottomLinks>
        </>
      )}
    </MobileMenuWrapper>
  );
};

export default NavMainMenuMobile;

const SocialIconsWrapper = styled.div`
  > section {
    justify-content: center !important;
    column-gap: 30px !important;
  }
`;

const MobileMenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const TopLinks = styled.div`
  padding: 20px 25px 0 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${Colors.cream};

  > div {
    width: 100%;
    max-width: 325px;
  }
`;

const BottomLinks = styled.div`
  width: 100%;
  padding: 0 25px 30px 25px;
  background-color: ${Colors.cream};
`;

const PolicyLinks = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 30px;
  margin-top: 30px;
  white-space: nowrap;
  justify-content: center;

  a {
    font-family: 'BrownStd';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0;
    text-align: left;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }
`;
